import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from './firebase';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';

const STRIPE_LINKS = {
  neon: 'https://buy.stripe.com/7sY28l1zjb8w6jXePZcMM0h',
  midnight: 'https://buy.stripe.com/cNi7sF1zjccA7o1cHRcMM0i',
  vintage: 'https://buy.stripe.com/9B6cMZ5Pz6Sg8s5dLVcMM0j',
  luxury: 'https://buy.stripe.com/14AaER7XH1xW9w96jtcMM0k',
  ocean: 'https://buy.stripe.com/9B614h0vf1xW9w9ePZcMM0l',
  'tb-midnight': 'https://buy.stripe.com/dRm4gt91L4K8gYBePZcMM0m',
  'tb-beach': 'https://buy.stripe.com/fZu5kx4Lv1xW8s537hcMM0n',
  'tb-wood': 'https://buy.stripe.com/14A7sFfq9ekIcIlgY7cMM0o',
  'tb-marble': 'https://buy.stripe.com/8x2fZbb9TgsQgYBazJcMM0p',
  'tb-neon': 'https://buy.stripe.com/eVqfZb1zj7WkfUx4blcMM0q',
  'avatar-fire': 'https://buy.stripe.com/3cIeV74LvdgEbEh37hcMM0r',
  'avatar-ice': 'https://buy.stripe.com/6oU8wJ4Lv2C0gYB23dcMM0s',
  'avatar-gold': 'https://buy.stripe.com/eVq14hcdXdgE4bP0Z9cMM0t',
  'avatar-shadow': 'https://buy.stripe.com/9B6dR30vf90oaAdbDNcMM0u',
  'avatar-royal': 'https://buy.stripe.com/9B628l91L1xW0ZDazJcMM0v',
  'bundle-starter': 'https://buy.stripe.com/9B65kx3Hr3G47o1azJcMM0w',
  'bundle-premium': 'https://buy.stripe.com/8x228lem53G4aAdfU3cMM0x',
};

const BUNDLE_ITEMS = {
  'bundle-starter': ['neon', 'midnight', 'tb-midnight'],
  'bundle-premium': ['luxury', 'ocean', 'vintage', 'tb-wood', 'tb-beach', 'avatar-fire'],
};

const SKINS = [
  { id: 'classic', name: 'Classic Blue', desc: 'The original', cssClass: 'cb-classic', symbol: '♦', price: 0, free: true },
  { id: 'neon', name: 'Neon Burst', desc: 'Vibrant gradient', cssClass: 'cb-neon', symbol: '★', price: 0.99 },
  { id: 'midnight', name: 'Midnight', desc: 'Deep purple', cssClass: 'cb-midnight', symbol: '◆', price: 0.99 },
  { id: 'vintage', name: 'Vintage', desc: 'Old school wood', cssClass: 'cb-vintage', symbol: '♣', price: 0.99 },
  { id: 'luxury', name: 'Luxury Gold', desc: 'Premium shine', cssClass: 'cb-luxury', symbol: '♛', price: 1.99, featured: true },
  { id: 'ocean', name: 'Ocean Breeze', desc: 'Cool blue waters', cssClass: 'cb-ocean', symbol: '〜', price: 0.99 },
];

const TABLES = [
  { id: 'felt', name: 'Green Felt', desc: 'Casino classic', cssClass: 'tb-felt', price: 0, free: true },
  { id: 'tb-midnight', name: 'Midnight', desc: 'Dark and sleek', cssClass: 'tb-midnight', price: 1.99 },
  { id: 'tb-beach', name: 'Beach', desc: 'Sandy vibes', cssClass: 'tb-beach', price: 1.99 },
  { id: 'tb-wood', name: 'Wood Grain', desc: 'Warm and natural', cssClass: 'tb-wood', price: 1.99, featured: true },
  { id: 'tb-marble', name: 'White Marble', desc: 'Elegant surface', cssClass: 'tb-marble', price: 1.99 },
  { id: 'tb-neon', name: 'Neon Grid', desc: 'Retro arcade', cssClass: 'tb-neon', price: 1.99 },
];

const AVATARS = [
  { id: 'avatar-default', name: 'Default', desc: 'Clean and simple', cssClass: 'av-default', symbol: '😊', price: 0, free: true },
  { id: 'avatar-fire', name: 'Fire', desc: 'Hot player', cssClass: 'av-fire', symbol: '🔥', price: 1.99 },
  { id: 'avatar-ice', name: 'Ice', desc: 'Cool under pressure', cssClass: 'av-ice', symbol: '❄️', price: 1.99 },
  { id: 'avatar-gold', name: 'Gold', desc: 'Champion energy', cssClass: 'av-gold', symbol: '👑', price: 1.99 },
  { id: 'avatar-shadow', name: 'Shadow', desc: 'Mysterious', cssClass: 'av-shadow', symbol: '🌑', price: 1.99 },
  { id: 'avatar-royal', name: 'Royal', desc: 'Born to win', cssClass: 'av-royal', symbol: '⚜️', price: 1.99 },
];

const BUNDLES = [
  { id: 'bundle-starter', name: 'Starter Pack', desc: '2 card backs + 1 table theme', price: 3.99, original: 5.97, save: '33%' },
  { id: 'bundle-premium', name: 'Premium Bundle', desc: '4 card backs + 2 tables + avatar pack', price: 7.99, original: 12.93, save: '38%', bestValue: true },
];

const S = {
  btn: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', width: '100%', marginTop: 6 },
  equipped: { textAlign: 'center', fontSize: 12, color: '#888', padding: '5px 0', marginTop: 6 },
  free: { fontSize: 11, background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: 6 },
};

export default function CosmeticsShop({ onClose }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('skins');
  const [owned, setOwned] = useState(new Set(['classic', 'felt', 'avatar-default']));
  const [equipped, setEquipped] = useState({ skin: 'classic', table: 'felt', avatar: 'avatar-default' });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user) {
      const loadProfile = async () => {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          if (data.cosmetics) {
            const ownedSet = new Set(['classic', 'felt', 'avatar-default']);
            (data.cosmetics.owned || []).forEach(id => ownedSet.add(id));
            setOwned(ownedSet);
            setEquipped({
              skin: data.cosmetics.activeSkin || 'classic',
              table: data.cosmetics.activeTable || 'felt',
              avatar: data.cosmetics.activeAvatar || 'avatar-default',
            });
          }
        }
      };
      loadProfile();
    }
  }, [user]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const unlockItem = async (id) => {
    if (BUNDLE_ITEMS[id]) {
      const items = BUNDLE_ITEMS[id];
      setOwned(prev => new Set([...prev, ...items]));
      if (user) {
        const ref = doc(db, 'users', user.uid);
        for (const itemId of items) {
          await updateDoc(ref, { 'cosmetics.owned': arrayUnion(itemId) });
        }
      }
      return;
    }
    setOwned(prev => new Set([...prev, id]));
    const isSkin = SKINS.find(s => s.id === id);
    const isTable = TABLES.find(t => t.id === id);
    const type = isSkin ? 'skin' : isTable ? 'table' : 'avatar';
    const key = type === 'skin' ? 'activeSkin' : type === 'table' ? 'activeTable' : 'activeAvatar';
    setEquipped(prev => ({ ...prev, [type]: id }));
    if (user) {
      const ref = doc(db, 'users', user.uid);
      await updateDoc(ref, {
        'cosmetics.owned': arrayUnion(id),
        [`cosmetics.${key}`]: id
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('purchase');
    const item = params.get('item');
    if (purchase === 'success' && item && user) {
      unlockItem(item);
      showToast(`🎉 ${item} unlocked!`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]);

  const equipItem = async (type, id, name) => {
    const key = type === 'skin' ? 'activeSkin' : type === 'table' ? 'activeTable' : 'activeAvatar';
    setEquipped(prev => ({ ...prev, [type]: id }));
    showToast(`${name} equipped!`);
    if (user) {
      const ref = doc(db, 'users', user.uid);
      await updateDoc(ref, { [`cosmetics.${key}`]: id });
    }
  };

  const buyItem = (id) => {
    const link = STRIPE_LINKS[id];
    if (!link) return;
    const userId = user?.uid || '';
    window.location.href = `${link}?client_reference_id=${userId}&metadata[itemId]=${id}&metadata[userId]=${userId}`;
  };

  const buyBundle = (id) => {
    const link = STRIPE_LINKS[id];
    if (!link) return;
    const userId = user?.uid || '';
    window.location.href = `${link}?client_reference_id=${userId}&metadata[itemId]=${id}&metadata[userId]=${userId}`;
  };

  const tabs = ['skins', 'tables', 'avatars', 'bundles'];

  const renderCard = (item, type) => {
    const isOwned = owned.has(item.id);
    const isEquipped = equipped[type] === item.id;
    return (
      <div key={item.id} style={{
        background: '#fff',
        border: isEquipped ? '2px solid #1D9E75' : '0.5px solid #e0e0e0',
        borderRadius: 12, padding: 12, textAlign: 'center', position: 'relative'
      }}>
        {item.featured && (
          <div style={{ position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', background: '#EF9F27', color: '#fff', fontSize: 10, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
            Most popular
          </div>
        )}
        {type === 'skin' && (
          <div className={item.cssClass} style={{ width: 46, height: 66, borderRadius: 6, margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'rgba(255,255,255,0.6)' }}>
            {item.symbol}
          </div>
        )}
        {type === 'table' && (
          <div className={item.cssClass} style={{ width: 80, height: 50, borderRadius: 6, margin: '0 auto 8px' }} />
        )}
        {type === 'avatar' && (
          <div className={item.cssClass} style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            {item.symbol}
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{item.name}</div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{item.desc}</div>
        {isOwned ? (
          isEquipped
            ? <div style={S.equipped}>✓ Equipped</div>
            : <button onClick={() => equipItem(type, item.id, item.name)} style={S.btn}>Equip</button>
        ) : item.free ? (
          <span style={S.free}>Free</span>
        ) : (
          <button onClick={() => buyItem(item.id)} style={S.btn}>
            ${item.price.toFixed(2)} — Buy
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '1rem', fontFamily: 'sans-serif', maxWidth: 640, margin: '0 auto' }}>
      {toast && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1D9E75', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999 }}>
          {toast}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: 18, fontWeight: 500 }}>Cosmetics Shop</h2>
        {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>✕</button>}
      </div>
      <div style={{ display: 'flex', gap: 4, background: '#f0f0f0', borderRadius: 8, padding: 4, marginBottom: '1.5rem' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: 7, fontSize: 13, border: activeTab === tab ? '0.5px solid #ddd' : 'none',
            borderRadius: 6, cursor: 'pointer',
            background: activeTab === tab ? '#fff' : 'none',
            fontWeight: activeTab === tab ? 500 : 400,
            color: activeTab === tab ? '#000' : '#666',
          }}>
            {tab === 'skins' ? 'Card backs' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'skins' && (
        <div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Free</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
            {SKINS.filter(s => s.free).map(s => renderCard(s, 'skin'))}
          </div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Premium</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
            {SKINS.filter(s => !s.free).map(s => renderCard(s, 'skin'))}
          </div>
        </div>
      )}

      {activeTab === 'tables' && (
        <div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Free</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
            {TABLES.filter(t => t.free).map(t => renderCard(t, 'table'))}
          </div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Premium</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
            {TABLES.filter(t => !t.free).map(t => renderCard(t, 'table'))}
          </div>
        </div>
      )}

      {activeTab === 'avatars' && (
        <div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Free</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
            {AVATARS.filter(a => a.free).map(a => renderCard(a, 'avatar'))}
          </div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Premium</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
            {AVATARS.filter(a => !a.free).map(a => renderCard(a, 'avatar'))}
          </div>
        </div>
      )}

      {activeTab === 'bundles' && (
        <div>
          {BUNDLES.map(b => (
            <div key={b.id} style={{
              background: '#fff', border: b.bestValue ? '2px solid #EF9F27' : '0.5px solid #e0e0e0',
              borderRadius: 12, padding: '1rem', marginBottom: 10, position: 'relative'
            }}>
              {b.bestValue && (
                <div style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', background: '#EF9F27', color: '#fff', fontSize: 10, padding: '2px 10px', borderRadius: 10, fontWeight: 500 }}>
                  Best value
                </div>
              )}
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{b.name}</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>{b.desc}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 18, fontWeight: 500 }}>${b.price.toFixed(2)}</div>
                <div style={{ fontSize: 13, color: '#aaa', textDecoration: 'line-through' }}>${b.original.toFixed(2)}</div>
                <div style={{ fontSize: 11, background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: 6 }}>Save {b.save}</div>
              </div>
              <button onClick={() => buyBundle(b.id)} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', width: '100%' }}>
                Buy bundle — ${b.price.toFixed(2)}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
